/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { jsx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import IconButton from '@atlaskit/button/icon/button';
import ZoomOutIcon from '@atlaskit/icon/core/zoom-out';
import ZoomInIcon from '@atlaskit/icon/core/zoom-in';
import FullscreenEnterIcon from '@atlaskit/icon/core/fullscreen-enter';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';

import { type ZoomLevel } from './zoomLevel.ts';
import { i18n } from './viewPdfMacroI18n.ts';

export type ZoomToolbarProps = {
	zoomLevel: ZoomLevel;
	onChange: (newZoomLevel: ZoomLevel) => void;
	onFitToPage: () => void;
	onExpandToggle: () => void;
	isFullscreen: boolean;
	isVisible?: boolean;
};

const styles = cssMap({
	container: {
		position: 'absolute',
		bottom: token('space.150'),
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.050'),
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		zIndex: 1,
		transition: 'opacity 0.2s ease',
	},
	containerVisible: {
		opacity: 1,
		pointerEvents: 'auto',
	},
	containerHidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
	zoomLabel: {
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
		cursor: 'pointer',
		paddingInline: token('space.050'),
		borderRadius: token('radius.small'),
		whiteSpace: 'nowrap',
		userSelect: 'none',
	},
});

export const ZoomToolbar = ({
	zoomLevel,
	onChange,
	onFitToPage,
	onExpandToggle,
	isFullscreen,
	isVisible: isVisibleProp,
}: ZoomToolbarProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isVisibleInternal, setIsVisibleInternal] = useState(false);
	const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const showToolbar = useCallback(() => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
		}
		setIsVisibleInternal(true);
	}, []);

	const scheduleHide = useCallback(() => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
		}
		hideTimerRef.current = setTimeout(() => {
			setIsVisibleInternal(false);
		}, 2000);
	}, []);

	useEffect(() => {
		return () => {
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
			}
		};
	}, []);

	// In fullscreen/presentation mode, always show the toolbar.
	// If isVisibleProp is provided by a parent, use that; otherwise use internal hover state.
	const isVisible = isVisibleProp !== undefined ? isVisibleProp : isVisibleInternal;
	const toolbarVisible = isFullscreen || isVisible;

	const handleZoomOut = useCallback(() => onChange(zoomLevel.zoomOut()), [onChange, zoomLevel]);
	const handleZoomIn = useCallback(() => onChange(zoomLevel.zoomIn()), [onChange, zoomLevel]);

	return (
		<div
			css={[styles.container, toolbarVisible ? styles.containerVisible : styles.containerHidden]}
			data-testid="viewpdf-zoom-toolbar"
			onMouseEnter={showToolbar}
			onMouseLeave={scheduleHide}
			onFocus={showToolbar}
			onBlur={scheduleHide}
		>
			<IconButton
				icon={ZoomOutIcon}
				label={formatMessage(i18n.zoomOut)}
				isDisabled={!zoomLevel.canZoomOut}
				onClick={handleZoomOut}
				appearance="subtle"
				spacing="compact"
				testId="viewpdf-zoom-out"
			/>
			<Pressable
				xcss={styles.zoomLabel}
				aria-label={formatMessage(i18n.fitToPage)}
				onClick={onFitToPage}
				onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
					if (e.key === 'Enter' || e.key === ' ') {
						onFitToPage();
					}
				}}
				testId="viewpdf-zoom-level"
			>
				{zoomLevel.asPercentage}
			</Pressable>
			<IconButton
				icon={ZoomInIcon}
				label={formatMessage(i18n.zoomIn)}
				isDisabled={!zoomLevel.canZoomIn}
				onClick={handleZoomIn}
				appearance="subtle"
				spacing="compact"
				testId="viewpdf-zoom-in"
			/>
			<IconButton
				icon={isFullscreen ? ShrinkDiagonalIcon : FullscreenEnterIcon}
				label={
					isFullscreen
						? formatMessage(i18n.exitPresentationMode)
						: formatMessage(i18n.presentationMode)
				}
				onClick={onExpandToggle}
				appearance="subtle"
				spacing="compact"
				testId="viewpdf-fullscreen-toggle"
			/>
		</div>
	);
};

/**
 * Hook that tracks hover state on a parent element to show/hide the zoom toolbar.
 * Attach the returned event handlers to the viewer container element.
 */
export const useZoomToolbarVisibility = (): {
	isVisible: boolean;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
} => {
	const [isVisible, setIsVisible] = useState(false);
	const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const onMouseEnter = useCallback((): void => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
		}
		setIsVisible(true);
	}, []);

	const onMouseLeave = useCallback((): void => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
		}
		hideTimerRef.current = setTimeout(() => {
			setIsVisible(false);
		}, 2000);
	}, []);

	useEffect(() => {
		return () => {
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
			}
		};
	}, []);

	return { isVisible, onMouseEnter, onMouseLeave };
};
